import React from "react";
import { useI18n } from "../i18n";
import Reveal from "../components/Reveal";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const Eyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] text-[#00B3B3]/90 uppercase">
    <span className="h-px w-8 bg-[#00B3B3]/60" />
    {children}
  </div>
);

const DASH_IMG = "https://static.prod-images.emergentagent.com/jobs/526565b4-e057-4988-9471-d86618ed67f7/images/cee9b6d2ea4b8bcb9f0d8f90c19b53e02803de6a9684dd242a260c5c662fe75b.png";

const STACK_PT = [
  { layer: "L4 · CONSUMPTION", items: ["SAP Analytics Cloud", "Power BI", "Tableau", "Embedded Analytics"] },
  { layer: "L3 · MODELING", items: ["SAP Datasphere", "BW/4HANA Composite Providers", "Calculation Views HANA"] },
  { layer: "L2 · STORAGE", items: ["SAP HANA Cloud", "BW/4HANA aDSO", "Data Lake"] },
  { layer: "L1 · INTEGRATION", items: ["SAP Data Intelligence", "SLT", "BODS", "APIs / OData"] },
  { layer: "L0 · SOURCES", items: ["S/4HANA", "ECC", "Salesforce", "Files / IoT", "Não-SAP"] },
];

const STACK_EN = [
  { layer: "L4 · CONSUMPTION", items: ["SAP Analytics Cloud", "Power BI", "Tableau", "Embedded Analytics"] },
  { layer: "L3 · MODELING", items: ["SAP Datasphere", "BW/4HANA Composite Providers", "Calculation Views HANA"] },
  { layer: "L2 · STORAGE", items: ["SAP HANA Cloud", "BW/4HANA aDSO", "Data Lake"] },
  { layer: "L1 · INTEGRATION", items: ["SAP Data Intelligence", "SLT", "BODS", "APIs / OData"] },
  { layer: "L0 · SOURCES", items: ["S/4HANA", "ECC", "Salesforce", "Files / IoT", "Non-SAP"] },
];

export default function Solutions() {
  const { t, lang } = useI18n();
  const stack = lang === "pt" ? STACK_PT : STACK_EN;
  return (
    <div data-testid="page-solutions" className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
      <Reveal><Eyebrow>{t.nav.solutions}</Eyebrow></Reveal>
      <Reveal delay={0.1}>
        <h1 className="mt-6 font-heading font-black tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl max-w-4xl leading-[1.05]">
          {lang === "pt" ? "Arquitetura de dados SAP, em camadas." : "SAP data architecture, layered by design."}
        </h1>
      </Reveal>
      <Reveal delay={0.2}>
        <p className="mt-6 text-white/60 max-w-2xl">
          {lang === "pt"
            ? "Modelo de referência da BEYONDCALCULUS para entregar plataformas de dados auditáveis, performáticas e escaláveis."
            : "BEYONDCALCULUS reference model to deliver auditable, performant and scalable data platforms."}
        </p>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-px bg-white/10 border border-white/10">
          {stack.map((row, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="bg-[#0a0a0a] p-6 grid grid-cols-1 md:grid-cols-4 gap-4 items-start hover:bg-[#111] transition-colors">
                <div className="font-mono text-xs tracking-widest text-[#00B3B3]">{row.layer}</div>
                <div className="md:col-span-3 flex flex-wrap gap-2">
                  {row.items.map((it) => (
                    <span key={it} className="text-sm text-white/85 border border-white/15 px-3 py-1.5">
                      {it}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.2}>
            <div className="relative border border-white/10 overflow-hidden">
              <img src={DASH_IMG} alt="reference dashboard" className="w-full h-auto" />
              <div className="absolute top-4 left-4 font-mono text-[10px] tracking-widest text-white/80 bg-black/60 px-2 py-1 border border-white/10">
                REFERENCE · SAP ANALYTICS CLOUD
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8 border border-white/10 p-6 bg-[#0c0c0c]">
              <div className="font-heading font-bold text-white">
                {lang === "pt" ? "Pronto para desenhar a sua?" : "Ready to design yours?"}
              </div>
              <p className="mt-2 text-white/60 text-sm">
                {lang === "pt"
                  ? "Em uma sessão de discovery mapeamos seu landscape SAP e propomos o blueprint ideal."
                  : "In a discovery session we map your SAP landscape and propose the ideal blueprint."}
              </p>
              <Link
                to="/contact"
                data-testid="solutions-cta"
                className="mt-4 inline-flex items-center gap-2 bg-[#00B3B3] text-white font-semibold px-5 py-2.5 rounded-sm hover:bg-[#00CCCC] transition-colors"
              >
                {t.nav.cta} <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
